import { Category, MapPoint, Service, TagGroups, DumpstationData } from '@/lib/types';
import { createContext, useContext, useEffect, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { usePage } from '@inertiajs/react';

export type Point = { id: number; lat: number; long: number; category_id: number };

type RefData = {
    categories?: Category[];
    services?: Service[];
    tags?: TagGroups;
    countries?: { id: number | string; name: string }[];
    dumpstation?: DumpstationData;
};

type PointContextType = {
    cache: MapPoint[] | undefined;
    setCache: Dispatch<SetStateAction<MapPoint[] | undefined>>;
    ref?: RefData;
    setRef: Dispatch<SetStateAction<RefData | undefined>>;
    clear: () => void;
};

const PointsContext = createContext<PointContextType | null>(null);

interface PointsCacheProviderProps {
    children: ReactNode;
}
export function PointsCacheProvider({ children }: PointsCacheProviderProps) {
    const page = usePage<any>();
    const [cache, setCache] = useState<MapPoint[] | undefined>(undefined);
    const [ref, setRef] = useState<RefData | undefined>(undefined);
    const clear = () => { setCache(undefined); setRef(undefined); };

    // 👇 Hydrera från sessionStorage en gång
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem('points:cache');
            if (raw) setCache(JSON.parse(raw) as MapPoint[]);
            const rawRef = sessionStorage.getItem('points:ref');
            if (rawRef) setRef(JSON.parse(rawRef) as RefData);
        } catch { }
    }, []);

    // 👇 Spegla till sessionStorage vid ändring
    useEffect(() => {
        try {
            if (cache === undefined) {
                sessionStorage.removeItem('points:cache');
            } else {
                sessionStorage.setItem('points:cache', JSON.stringify(cache));
            }
        } catch { }
    }, [cache]);

    // Mirror refdata
    useEffect(() => {
        try {
            if (!ref) sessionStorage.removeItem('points:ref');
            else sessionStorage.setItem('points:ref', JSON.stringify(ref));
        } catch { }
    }, [ref]);

    // Hydrate refdata from Inertia shared props if present
    useEffect(() => {
        try {
            const sharedRef = page?.props?.data as RefData | undefined;
            if (!sharedRef) return;
            setRef(prev => ({ ...(prev || {}), ...sharedRef }));
        } catch { /* ignore */ }
    }, [page?.props?.ref]);

    return (
        <PointsContext.Provider value={{ cache, setCache, ref, setRef, clear }}>
            {children}
        </PointsContext.Provider>
    );
}

export function usePointsCache() {
    const context = useContext(PointsContext);
    if (!context) throw new Error('PointsCacheProvider saknas i trädet.');
    return context;
}
